import type { PreferenceProfile, ReaderPreference } from "../api";

export type ReaderDeviceKind = "windows" | "macos" | "linux" | "android" | "ios" | "ipad" | "mobile" | "desktop";

export interface ReaderDeviceSignal {
  userAgent?: string;
  platform?: string;
  width?: number;
  height?: number;
  maxTouchPoints?: number;
  coarsePointer?: boolean;
}

const devicePreferenceNames: Record<ReaderDeviceKind, string> = {
  windows: "默认阅读（Windows）",
  macos: "默认阅读（macOS）",
  linux: "默认阅读（Linux）",
  android: "默认阅读（Android）",
  ios: "默认阅读（iOS）",
  ipad: "默认阅读（iPad）",
  mobile: "默认阅读（移动端）",
  desktop: "默认阅读（桌面端）",
};

const deviceThemeStorageKeys: Record<ReaderDeviceKind, string> = {
  windows: "reader.theme.windows",
  macos: "reader.theme.macos",
  linux: "reader.theme.linux",
  android: "reader.theme.android",
  ios: "reader.theme.ios",
  ipad: "reader.theme.ipad",
  mobile: "reader.theme.mobile",
  desktop: "reader.theme.desktop",
};
const devicePreferenceStorageKeys: Record<ReaderDeviceKind, string> = {
  windows: "reader.preference.windows",
  macos: "reader.preference.macos",
  linux: "reader.preference.linux",
  android: "reader.preference.android",
  ios: "reader.preference.ios",
  ipad: "reader.preference.ipad",
  mobile: "reader.preference.mobile",
  desktop: "reader.preference.desktop",
};

const basePreferenceName = "默认阅读";
const legacyDevicePreferenceNames: Partial<Record<ReaderDeviceKind, string[]>> = {
  windows: ["默认阅读（PC）"],
  macos: ["默认阅读（PC）"],
  linux: ["默认阅读（PC）"],
  desktop: ["默认阅读（PC）"],
  android: ["默认阅读（手机）"],
  ios: ["默认阅读（手机）"],
  ipad: ["默认阅读（平板）"],
  mobile: ["默认阅读（手机）"],
};
const legacyThemeStorageKeys: Partial<Record<ReaderDeviceKind, string[]>> = {
  windows: ["reader.theme.desktop"],
  macos: ["reader.theme.desktop"],
  linux: ["reader.theme.desktop"],
  desktop: ["reader.theme.desktop"],
  android: ["reader.theme.mobile"],
  ios: ["reader.theme.mobile"],
  ipad: ["reader.theme.tablet"],
  mobile: ["reader.theme.mobile"],
};
export const legacyThemeStorageKey = "reader.theme";

export function getCurrentReaderDeviceKind(): ReaderDeviceKind {
  if (typeof window === "undefined") return "desktop";

  return resolveReaderDeviceKind({
    userAgent: window.navigator.userAgent,
    platform: window.navigator.platform,
    width: window.innerWidth,
    height: window.innerHeight,
    maxTouchPoints: window.navigator.maxTouchPoints,
    coarsePointer: window.matchMedia?.("(pointer: coarse)").matches,
  });
}

export function resolveReaderDeviceKind(signal: ReaderDeviceSignal): ReaderDeviceKind {
  const userAgent = signal.userAgent?.toLowerCase() ?? "";
  const platform = signal.platform?.toLowerCase() ?? "";
  const width = signal.width ?? 0;
  const height = signal.height ?? 0;
  const minSide = Math.min(width || Number.POSITIVE_INFINITY, height || Number.POSITIVE_INFINITY);
  const maxSide = Math.max(width, height);
  const touchPoints = signal.maxTouchPoints ?? 0;
  const hasTouch = Boolean(signal.coarsePointer || touchPoints);

  if (/ipad|tablet|playbook|silk/i.test(userAgent) || (platform === "macintel" && touchPoints > 1 && minSide >= 600)) return "ipad";
  if (/android/i.test(userAgent)) return "android";
  if (/iphone|ipod/i.test(userAgent)) return "ios";
  if (/windows phone/i.test(userAgent)) return "mobile";
  if (/windows/i.test(userAgent) || platform.startsWith("win")) return "windows";
  if (/macintosh|mac os x/i.test(userAgent) || platform.startsWith("mac")) return "macos";
  if (/linux/i.test(userAgent) || platform.startsWith("linux")) return "linux";
  if (/mobi|phone/i.test(userAgent)) return "mobile";
  if (hasTouch && maxSide > 0 && minSide < 600) return "mobile";
  if (width > 0 && width < 700) return "mobile";

  return "desktop";
}

export function getReaderDevicePreferenceName(deviceKind: ReaderDeviceKind): string {
  return devicePreferenceNames[deviceKind];
}

export function getReaderThemeStorageKey(deviceKind: ReaderDeviceKind, userId?: string): string {
  const key = deviceThemeStorageKeys[deviceKind];

  return userId ? `${key}.${encodeURIComponent(userId)}` : key;
}

export function getReaderPreferenceStorageKey(deviceKind: ReaderDeviceKind, userId: string): string {
  return `${devicePreferenceStorageKeys[deviceKind]}.${encodeURIComponent(userId)}`;
}

export function selectReaderDevicePreferenceProfile(
  preferences: PreferenceProfile[],
  activePreference: PreferenceProfile,
  deviceKind: ReaderDeviceKind,
): { profile: PreferenceProfile; matchedDevice: boolean } {
  const name = getReaderDevicePreferenceName(deviceKind);
  const profile = preferences.find(item => item.name === name);
  const legacyProfile = legacyDevicePreferenceNames[deviceKind]
    ?.map(legacyName => preferences.find(item => item.name === legacyName))
    .find(Boolean);
  const activeIsOtherDeviceProfile = isReaderDevicePreferenceName(activePreference.name);
  const baseProfile = activeIsOtherDeviceProfile ? preferences.find(item => item.name === basePreferenceName) : undefined;

  return {
    profile: profile ?? legacyProfile ?? baseProfile ?? activePreference,
    matchedDevice: Boolean(profile),
  };
}

export function readStoredDeviceTheme(
  storage: Pick<Storage, "getItem" | "setItem">,
  deviceKind: ReaderDeviceKind,
  userId?: string,
): ReaderPreference["theme"] | null {
  const key = getReaderThemeStorageKey(deviceKind, userId);
  const stored = parseReaderTheme(storage.getItem(key));
  if (stored) return stored;

  for (const legacyKey of getLegacyThemeStorageKeys(deviceKind, userId)) {
    const legacyStored = parseReaderTheme(storage.getItem(legacyKey));
    if (!legacyStored) continue;
    storage.setItem(key, legacyStored);
    return legacyStored;
  }

  if (userId) return null;

  const legacyTheme = parseReaderTheme(storage.getItem(legacyThemeStorageKey));
  if (!legacyTheme) return null;

  // 旧版本只有全局主题 首次读取时迁移到当前设备类型
  storage.setItem(key, legacyTheme);
  return legacyTheme;
}

export function parseReaderTheme(value: string | null): ReaderPreference["theme"] | null {
  return value === "dark" || value === "light" ? value : null;
}

export function readStoredDevicePreference(
  storage: Pick<Storage, "getItem" | "removeItem">,
  deviceKind: ReaderDeviceKind,
  userId: string,
): Partial<ReaderPreference> | null {
  const key = getReaderPreferenceStorageKey(deviceKind, userId);
  const raw = storage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;
    const preference = isRecord(parsed) && parsed.version === 1 && isRecord(parsed.preference) ? parsed.preference : null;
    if (!preference) throw new Error("Invalid preference draft");

    return parseStoredReaderPreference(preference);
  } catch {
    storage.removeItem(key);
    return null;
  }
}

export function writeStoredDevicePreference(
  storage: Pick<Storage, "setItem">,
  deviceKind: ReaderDeviceKind,
  userId: string,
  preference: ReaderPreference,
) {
  storage.setItem(getReaderPreferenceStorageKey(deviceKind, userId), JSON.stringify({
    version: 1,
    preference,
  }));
}

export function clearStoredDevicePreference(storage: Pick<Storage, "removeItem">, deviceKind: ReaderDeviceKind, userId: string) {
  storage.removeItem(getReaderPreferenceStorageKey(deviceKind, userId));
}

function isReaderDevicePreferenceName(name: string): boolean {
  return Object.values(devicePreferenceNames).includes(name);
}

function getLegacyThemeStorageKeys(deviceKind: ReaderDeviceKind, userId?: string): string[] {
  return (legacyThemeStorageKeys[deviceKind] ?? [])
    .map(key => userId ? `${key}.${encodeURIComponent(userId)}` : key)
    .filter(key => key !== getReaderThemeStorageKey(deviceKind, userId));
}

function parseStoredReaderPreference(value: Record<string, unknown>): Partial<ReaderPreference> {
  const preference: Partial<ReaderPreference> = {};

  assignTheme(preference, value, "theme");
  assignString(preference, value, "fontFamily");
  assignNumber(preference, value, "fontWeight");
  assignScript(preference, value, "script");
  assignNumber(preference, value, "fontSize");
  assignNumber(preference, value, "letterSpacing");
  assignNumber(preference, value, "lineHeight");
  assignNumber(preference, value, "paragraphSpacing");
  assignNumber(preference, value, "textIndent");
  assignTextAlign(preference, value, "textAlign");
  assignTextAlignLast(preference, value, "textAlignLast");
  assignNumber(preference, value, "paddingTop");
  assignNumber(preference, value, "paddingRight");
  assignNumber(preference, value, "paddingBottom");
  assignNumber(preference, value, "paddingLeft");
  assignNumber(preference, value, "contentWidth");
  assignString(preference, value, "lightTextColor");
  assignString(preference, value, "darkTextColor");
  assignString(preference, value, "lightBackgroundColor");
  assignString(preference, value, "darkBackgroundColor");
  assignString(preference, value, "backgroundImageUrl");
  assignBoolean(preference, value, "autoPage");
  assignNumber(preference, value, "prefetchChapterCount");

  return preference;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function assignTheme(preference: Partial<ReaderPreference>, value: Record<string, unknown>, key: "theme") {
  if (value[key] === "dark" || value[key] === "light") preference[key] = value[key];
}

function assignString(
  preference: Partial<ReaderPreference>,
  value: Record<string, unknown>,
  key: "fontFamily" | "lightTextColor" | "darkTextColor" | "lightBackgroundColor" | "darkBackgroundColor" | "backgroundImageUrl",
) {
  if (typeof value[key] === "string") preference[key] = value[key];
}

function assignNumber(
  preference: Partial<ReaderPreference>,
  value: Record<string, unknown>,
  key:
    | "fontWeight"
    | "fontSize"
    | "letterSpacing"
    | "lineHeight"
    | "paragraphSpacing"
    | "textIndent"
    | "paddingTop"
    | "paddingRight"
    | "paddingBottom"
    | "paddingLeft"
    | "contentWidth"
    | "prefetchChapterCount",
) {
  if (typeof value[key] === "number" && Number.isFinite(value[key])) preference[key] = value[key];
}

function assignBoolean(preference: Partial<ReaderPreference>, value: Record<string, unknown>, key: "autoPage") {
  if (typeof value[key] === "boolean") preference[key] = value[key];
}

function assignScript(preference: Partial<ReaderPreference>, value: Record<string, unknown>, key: "script") {
  if (value[key] === "source" || value[key] === "simplified" || value[key] === "traditional") preference[key] = value[key];
}

function assignTextAlign(preference: Partial<ReaderPreference>, value: Record<string, unknown>, key: "textAlign") {
  if (value[key] === "start" || value[key] === "justify") preference[key] = value[key];
}

function assignTextAlignLast(preference: Partial<ReaderPreference>, value: Record<string, unknown>, key: "textAlignLast") {
  if (value[key] === "auto" || value[key] === "start" || value[key] === "justify") preference[key] = value[key];
}
